/** WebSocket client for real-time dashboard updates. */

import { getAccessToken } from '../api/client'
import { config } from '../config'

type Channel = 'positions' | 'orders' | 'risk' | 'alerts' | 'system' | 'market_data'

interface ClientMessage {
  type: 'auth' | 'subscribe' | 'unsubscribe' | 'ping'
  token?: string
  channels?: Channel[]
  ts?: number
}

export interface ServerMessage {
  type: 'auth_ok' | 'auth_fail' | 'snapshot' | 'update' | 'pong' | 'error'
  user_id?: string
  reason?: string
  channel?: Channel
  data?: unknown
  ts?: number
  server_ts?: number
  message?: string
}

type MessageHandler = (msg: ServerMessage) => void
type ConnectionHandler = () => void

/** Reconnecting WebSocket client with auto-auth and channel subscriptions. */
export class WsClient {
  private ws: WebSocket | null = null
  private url: string
  private handlers: Map<string, Set<MessageHandler>> = new Map()
  private onConnectHandlers: Set<ConnectionHandler> = new Set()
  private onDisconnectHandlers: Set<ConnectionHandler> = new Set()
  private subscriptions: Set<Channel> = new Set()
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null
  private pingTimer: ReturnType<typeof setInterval> | null = null
  private reconnectDelay = 1000
  private maxReconnectDelay = 30000
  private authenticated = false
  private _isConnected = false
  private shouldReconnect = true

  constructor(url?: string) {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
    this.url = url ?? `${protocol}//${window.location.host}${config.wsUrl}`
  }

  get isConnected(): boolean {
    return this._isConnected
  }

  get isAuthenticated(): boolean {
    return this.authenticated
  }

  /** Connect to the WebSocket server. */
  connect(): void {
    if (this.ws?.readyState === WebSocket.OPEN) return

    this.shouldReconnect = true
    this.ws = new WebSocket(this.url)

    this.ws.onopen = () => {
      this._isConnected = true
      this.reconnectDelay = 1000
      this.onConnectHandlers.forEach((h) => h())

      // Auto-authenticate
      const token = getAccessToken()
      if (token) {
        this.send({ type: 'auth', token })
      }

      // Start ping interval (30s)
      this.pingTimer = setInterval(() => {
        this.send({ type: 'ping', ts: Date.now() })
      }, 30000)
    }

    this.ws.onmessage = (event) => {
      try {
        const msg: ServerMessage = JSON.parse(event.data)
        this.handleMessage(msg)
      } catch {
        console.error('Failed to parse WS message:', event.data)
      }
    }

    this.ws.onclose = () => {
      this._isConnected = false
      this.authenticated = false
      this.clearPingTimer()
      this.onDisconnectHandlers.forEach((h) => h())
      if (this.shouldReconnect) {
        this.scheduleReconnect()
      }
    }

    this.ws.onerror = () => {
      // onclose will fire after onerror
    }
  }

  /** Disconnect and stop reconnecting. */
  disconnect(): void {
    this.shouldReconnect = false
    this.clearReconnectTimer()
    this.clearPingTimer()
    this.ws?.close()
    this.ws = null
    this._isConnected = false
    this.authenticated = false
  }

  /** Subscribe to one or more channels. */
  subscribe(channels: Channel[]): void {
    channels.forEach((ch) => this.subscriptions.add(ch))
    if (this.authenticated) {
      this.send({ type: 'subscribe', channels })
    }
  }

  /** Unsubscribe from one or more channels. */
  unsubscribe(channels: Channel[]): void {
    channels.forEach((ch) => this.subscriptions.delete(ch))
    if (this.authenticated) {
      this.send({ type: 'unsubscribe', channels })
    }
  }

  /** Register a handler for a specific message type. */
  on(type: string, handler: MessageHandler): () => void {
    if (!this.handlers.has(type)) {
      this.handlers.set(type, new Set())
    }
    this.handlers.get(type)!.add(handler)
    return () => this.handlers.get(type)?.delete(handler)
  }

  /** Register a handler for connection events. */
  onConnect(handler: ConnectionHandler): () => void {
    this.onConnectHandlers.add(handler)
    return () => this.onConnectHandlers.delete(handler)
  }

  /** Register a handler for disconnection events. */
  onDisconnect(handler: ConnectionHandler): () => void {
    this.onDisconnectHandlers.add(handler)
    return () => this.onDisconnectHandlers.delete(handler)
  }

  private send(msg: ClientMessage): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(msg))
    }
  }

  private handleMessage(msg: ServerMessage): void {
    // Handle auth response
    if (msg.type === 'auth_ok') {
      this.authenticated = true
      // Re-subscribe to channels after auth
      if (this.subscriptions.size > 0) {
        this.send({
          type: 'subscribe',
          channels: Array.from(this.subscriptions),
        })
      }
    } else if (msg.type === 'auth_fail') {
      this.authenticated = false
    }

    // Dispatch to type-specific handlers
    const handlers = this.handlers.get(msg.type)
    if (handlers) {
      handlers.forEach((h) => h(msg))
    }

    // Also dispatch to wildcard handlers
    const wildcardHandlers = this.handlers.get('*')
    if (wildcardHandlers) {
      wildcardHandlers.forEach((h) => h(msg))
    }
  }

  private scheduleReconnect(): void {
    this.clearReconnectTimer()
    this.reconnectTimer = setTimeout(() => {
      this.connect()
      this.reconnectDelay = Math.min(this.reconnectDelay * 2, this.maxReconnectDelay)
    }, this.reconnectDelay)
  }

  private clearReconnectTimer(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }
  }

  private clearPingTimer(): void {
    if (this.pingTimer) {
      clearInterval(this.pingTimer)
      this.pingTimer = null
    }
  }
}

/** Singleton WebSocket client instance. */
export const wsClient = new WsClient()
